'use client';

import { useEffect, type FC } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import mlAccordion from './ml-accordion.variants';

// Components
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtNavLink } from '@/components/atoms/at-nav-link/at-nav-link';

// Types
import {
  EAccordionItemIntents,
  IMlAccordionItemProps,
  IMlAccordionProps,
} from './ml-accordion.types';
import { EClickableSize } from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { ENavLinkVariants } from '@/components/atoms/at-nav-link/at-nav-link.types';

// Hooks / Context
import { AccordionProvider, useAccordion } from '@/context/accordionContext';

// Utils
import { sendGTMEvent } from '@next/third-parties/google';

/**
 * MlAccordion is a context provider wrapper for a set of accordion items.
 * It ensures only one item is open at a time and initializes Invoca on mount.
 *
 */
export const MlAccordion: FC<IMlAccordionProps> = ({
  children,
  className,
  dataTestId,
}) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Replaces phone number in footer
      window?.Invoca?.PNAPI?.run();
    }
  }, []);

  return (
    <div className={className} data-testid={dataTestId}>
      <AccordionProvider>{children}</AccordionProvider>
    </div>
  );
};

/**
 * MlAccordionItem represents a single expandable accordion item.
 * It uses shared state from AccordionProvider and supports two display intents.
 *
 */
export const MlAccordionItem: React.FC<IMlAccordionItemProps> = ({
  index,
  title,
  children,
  intent = EAccordionItemIntents.DEFAULT,
}) => {
  const { expanded, toggleAccordion } = useAccordion();
  const isOpen = index === expanded;

  const gtmLocation = EAccordionItemIntents.DEFAULT
    ? 'Frequently Asked Questions'
    : 'Nav - Mobile';

  const { accordionItemBase, accordionItemBody, icon } = mlAccordion({
    intents: intent,
    open: isOpen,
  });

  const handleClick = (accordionTitle: string) => {
    sendGTMEvent({
      event: 'accordion_interaction',
      value: {
        click_action: isOpen ? 'close' : 'open',
        click_text: accordionTitle,
        interaction_location: gtmLocation,
      },
    });
    toggleAccordion(isOpen ? null : index);
  };
  return (
    <div className={accordionItemBase()}>
      {intent === EAccordionItemIntents.DEFAULT && (
        <motion.button
          role="button"
          tabIndex={0}
          initial={false}
          onClick={() => handleClick(title)}
          onKeyDown={(e) => e.key === 'Enter' && handleClick(title)}
          aria-expanded={isOpen}
          aria-controls={`section-${index}`}
          aria-labelledby={`accordion-header-${index}`}
          className="flex w-full flex-row items-center justify-between gap-10 pb-5 pt-8 text-2xl md:pb-8 md:pt-12"
        >
          <AtHeader
            element="span"
            variant="h4"
            className="text-left font-Figtree text-skin-secondary"
          >
            {title}
          </AtHeader>

          <AtIcon
            icon={EIcons.CHEVRON}
            color="var(--color-ui-fill-primary)"
            className={classNames(
              icon,
              'min-w-6 transform transition-transform',
              { 'rotate-180': isOpen },
            )}
          />
        </motion.button>
      )}

      {intent === EAccordionItemIntents.NAV && (
        <>
          <AtNavLink
            variant={ENavLinkVariants.CTA}
            size={EClickableSize.LG}
            label={title}
            onClick={() => handleClick(title)}
            aria-expanded={isOpen}
            aria-controls={`section-${index}`}
            aria-labelledby={`accordion-header-${index}`}
            selected={isOpen}
            className="flex w-full flex-row items-center justify-between gap-10 !py-4.5 pb-5 pt-8 text-left !text-20 text-2xl md:pb-8 md:pt-12 [&>span]:w-full [&>span]:justify-between"
          >
            <AtIcon
              icon={EIcons.CHEVRON}
              color="var(--color-ui-knockout-primary)"
              className={classNames(
                icon,
                'min-w-6 transform transition-transform',
                { 'rotate-180': isOpen },
              )}
            />
          </AtNavLink>
        </>
      )}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.section
            id={`section-${index}`}
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            className={accordionItemBody()}
            variants={{
              open: {
                opacity: 1,
                height: 'auto',
                marginTop: 28,
                marginBottom: 28,
              },
              collapsed: {
                opacity: 0,
                height: 0,
                marginTop: 0,
                marginBottom: 0,
              },
            }}
            transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
            aria-labelledby={`accordion-body-${index}`}
          >
            {children}
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};
